/**
 * Dispatch · Dialogs (extracted from index.jsx for cohesion).
 *
 * All modal dialogs: Create, Allocate, Seal, Docs, PhotoCapture, PhotoGallery,
 * Optimiser, DockBooking, ManualShipping, DockHeatmap, CarrierHandoff.
 */
import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Plus, Truck, Package, Barcode, Warning, Check, ClipboardText, FileText,
  Eye, X, CaretRight, ListChecks, CircleNotch, Lock, Prohibit, Pause,
  PaperPlaneTilt, ArrowUUpLeft, Trash, DownloadSimple, Camera, ImageSquare,
  Printer, Lightning, Gauge, CalendarCheck, Truck as TruckIcon,
  ShieldCheck, ArrowsCounterClockwise,
} from '@phosphor-icons/react';
import {
  createDispatch, allocateToDispatch, sealDispatch, uploadDispatchDoc,
  listDispatchDocs, deleteDispatchDoc, dispatchDocDownloadUrl, deallocateDispatchOrder,
  loadScanDispatch, addDispatchPhoto, listDispatchPhotos, deleteDispatchPhoto,
  getDispatchOptimise, listDocks, bookDock, releaseDock, bookManualShipping,
  removeManualShipping, dispatchManifestPdfUrl, dispatchBolPdfUrl,
  getDispatchLoadSequence, markDispatchDelivered, markDispatchReturned,
  getDocksHeatmap, heatmapAutoResolve, generateCarrierHandoff,
  listCarrierHandoffs, revokeCarrierHandoff,
  getOrders, allocateDispatchOrder, getDispatchDoc,
} from '../../lib/api';
import { CONTAINER_TYPES, LOAD_TYPES, STATUS_META, NEXT_STATUS, SumItem, SummaryBar, DashCard } from './_shared';

export const CreateDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({
    customer_name: '', container_type: '40GP', load_type: 'customer_order',
    destination: '', carrier: '', transport_company: '', container_number: '',
    trailer_rego: '', driver_name: '', driver_phone: '', planned_dispatch_at: '',
    reefer_temp_c: '', dg_flag: false, notes: '',
  });
  const submit = async () => {
    if (!form.customer_name.trim()) return toast.error('Customer name is required');
    try {
      const payload = {
        ...form,
        planned_dispatch_at: form.planned_dispatch_at ? new Date(form.planned_dispatch_at).toISOString() : null,
        reefer_temp_c: form.reefer_temp_c !== '' ? Number(form.reefer_temp_c) : null,
      };
      const { data } = await createDispatch(payload);
      toast.success(`Created ${data.dispatch_number}`);
      onCreated(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border border-border rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-dispatch-dialog">
        <DialogHeader>
          <DialogTitle>New Dispatch Booking</DialogTitle>
          <DialogDescription className="text-xs">Container details + driver + schedule — you can allocate orders after creation.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Customer *</Label>
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="rounded-none" data-testid="create-customer" />
          </div>
          <div>
            <Label>Container type</Label>
            <Select value={form.container_type} onValueChange={(v) => setForm({ ...form, container_type: v })}>
              <SelectTrigger data-testid="create-container-type" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>{CONTAINER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Load type</Label>
            <Select value={form.load_type} onValueChange={(v) => setForm({ ...form, load_type: v })}>
              <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>{LOAD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Destination</Label>
            <Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className="rounded-none" />
          </div>
          <div>
            <Label>Container #</Label>
            <Input value={form.container_number} onChange={(e) => setForm({ ...form, container_number: e.target.value })} className="rounded-none font-mono" />
          </div>
          <div>
            <Label>Carrier</Label>
            <Input value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} className="rounded-none" />
          </div>
          <div>
            <Label>Transport co</Label>
            <Input value={form.transport_company} onChange={(e) => setForm({ ...form, transport_company: e.target.value })} className="rounded-none" />
          </div>
          <div>
            <Label>Trailer rego</Label>
            <Input value={form.trailer_rego} onChange={(e) => setForm({ ...form, trailer_rego: e.target.value })} className="rounded-none font-mono" />
          </div>
          <div>
            <Label>Driver name</Label>
            <Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} className="rounded-none" />
          </div>
          <div>
            <Label>Driver phone</Label>
            <Input value={form.driver_phone} onChange={(e) => setForm({ ...form, driver_phone: e.target.value })} className="rounded-none font-mono" />
          </div>
          <div>
            <Label>Planned dispatch</Label>
            <Input type="datetime-local" value={form.planned_dispatch_at} onChange={(e) => setForm({ ...form, planned_dispatch_at: e.target.value })} className="rounded-none" />
          </div>
          {form.container_type.includes('REEFER') && (
            <div>
              <Label>Reefer temp (°C)</Label>
              <Input type="number" value={form.reefer_temp_c} onChange={(e) => setForm({ ...form, reefer_temp_c: e.target.value })} className="rounded-none" />
            </div>
          )}
          <div className="flex items-center gap-2 mt-6">
            <input id="dg_flag" type="checkbox" checked={form.dg_flag} onChange={(e) => setForm({ ...form, dg_flag: e.target.checked })} />
            <Label htmlFor="dg_flag" className="text-xs">Dangerous goods</Label>
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-none" />
          </div>
        </div>
        <Button onClick={submit} data-testid="submit-dispatch-btn" className="bg-brand text-white rounded-none w-full">
          Create booking
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Allocate dialog ----------
export const AllocateDialog = ({ open, onOpenChange, dispatchId, existing, onAllocated }) => {
  const [orders, setOrders] = useState([]);
  const [orderId, setOrderId] = useState('');
  const [items, setItems] = useState([]);
  const existingIds = new Set((existing || []).map(a => a.order_id));

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await getOrders({ status: 'pending' });
        setOrders((data || []).filter(o => !existingIds.has(o.id)));
      } catch { toast.error('Failed to load orders'); }
    })();
    // eslint-disable-next-line
  }, [open]);

  const onOrderSelect = (oid) => {
    setOrderId(oid);
    const o = orders.find(x => x.id === oid);
    setItems((o?.items || []).map(it => ({
      sku_code: it.sku_code,
      variant_id: it.variant_id,
      qty: it.quantity,
      weight_kg_each: 0,
      volume_m3_each: 0,
    })));
  };

  const submit = async () => {
    if (!orderId) return toast.error('Pick an order');
    const payload = { order_id: orderId, items: items.filter(i => i.qty > 0) };
    try {
      await allocateDispatchOrder(dispatchId, payload);
      toast.success('Allocated');
      setOrderId(''); setItems([]);
      onAllocated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Allocate failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border border-border rounded-none max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="allocate-dialog">
        <DialogHeader>
          <DialogTitle>Allocate order to dispatch</DialogTitle>
          <DialogDescription className="text-xs">Pick a pending order and adjust qty + weight/volume per line.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Order</Label>
            <Select value={orderId} onValueChange={onOrderSelect}>
              <SelectTrigger data-testid="allocate-order-select" className="rounded-none"><SelectValue placeholder="Select order…" /></SelectTrigger>
              <SelectContent>
                {orders.length === 0 ? (
                  <div className="p-2 text-xs text-muted-foreground">No pending orders available.</div>
                ) : (
                  orders.map(o => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.order_number} · {o.customer_name} · {(o.items || []).length} line(s)
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          {items.length > 0 && (
            <table className="w-full table-dense">
              <thead>
                <tr><th>SKU</th><th className="text-right">Qty</th><th className="text-right">Wt/ea (kg)</th><th className="text-right">Vol/ea (m³)</th></tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i}>
                    <td className="font-mono font-bold">{it.sku_code}</td>
                    <td className="text-right">
                      <Input type="number" value={it.qty} onChange={(e) => {
                        const n = [...items]; n[i] = { ...it, qty: Number(e.target.value) }; setItems(n);
                      }} className="rounded-none w-20 inline-block text-right" />
                    </td>
                    <td className="text-right">
                      <Input type="number" step="0.01" value={it.weight_kg_each} onChange={(e) => {
                        const n = [...items]; n[i] = { ...it, weight_kg_each: Number(e.target.value) }; setItems(n);
                      }} className="rounded-none w-24 inline-block text-right" />
                    </td>
                    <td className="text-right">
                      <Input type="number" step="0.001" value={it.volume_m3_each} onChange={(e) => {
                        const n = [...items]; n[i] = { ...it, volume_m3_each: Number(e.target.value) }; setItems(n);
                      }} className="rounded-none w-24 inline-block text-right" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Button onClick={submit} disabled={!orderId} data-testid="submit-allocate-btn" className="bg-brand text-white rounded-none w-full">
            Allocate
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Seal dialog ----------
export const SealDialog = ({ open, onOpenChange, dispatchId, reefer, dgFlag, onSealed }) => {
  const [seals, setSeals] = useState('');
  const [condition, setCondition] = useState(true);
  const [damage, setDamage] = useState('');
  const [temp, setTemp] = useState('');
  const [dgVerified, setDgVerified] = useState(false);
  const [signedName, setSignedName] = useState('');

  const submit = async () => {
    const seal_numbers = seals.split(/[,\s]+/).filter(Boolean);
    if (seal_numbers.length === 0) return toast.error('Enter at least one seal number');
    if (dgFlag && !dgVerified) return toast.error('DG verification required for this container');
    try {
      await sealDispatch(dispatchId, {
        seal_numbers,
        condition_ok: condition,
        damage_notes: damage || null,
        temperature_c: temp !== '' ? Number(temp) : null,
        dg_verified: dgVerified,
        signed_name: signedName || null,
      });
      toast.success('Sealed');
      onSealed();
      setSeals(''); setDamage(''); setTemp(''); setSignedName(''); setDgVerified(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Seal failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border border-border rounded-none max-w-lg" data-testid="seal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock size={18} weight="fill" className="text-emerald-600" /> Apply seal & compliance checklist
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Seal numbers (comma or space separated)</Label>
            <Input value={seals} onChange={(e) => setSeals(e.target.value)} className="rounded-none font-mono" data-testid="seal-numbers" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="cond" checked={condition} onChange={(e) => setCondition(e.target.checked)} />
            <Label htmlFor="cond" className="text-xs">Container condition OK</Label>
          </div>
          {!condition && (
            <div>
              <Label>Damage notes</Label>
              <Textarea rows={2} value={damage} onChange={(e) => setDamage(e.target.value)} className="rounded-none" />
            </div>
          )}
          {reefer && (
            <div>
              <Label>Reefer temperature (°C)</Label>
              <Input type="number" value={temp} onChange={(e) => setTemp(e.target.value)} className="rounded-none" />
            </div>
          )}
          {dgFlag && (
            <div className="border border-amber-300 bg-amber-50 p-2 flex items-center gap-2">
              <input type="checkbox" id="dg" checked={dgVerified} onChange={(e) => setDgVerified(e.target.checked)} data-testid="dg-verified" />
              <Label htmlFor="dg" className="text-xs text-amber-900 font-bold">DG compliance verified</Label>
            </div>
          )}
          <div>
            <Label>Signed name</Label>
            <Input value={signedName} onChange={(e) => setSignedName(e.target.value)} className="rounded-none" placeholder="(defaults to your name)" />
          </div>
          <Button onClick={submit} data-testid="submit-seal-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none w-full">
            Apply seal & checklist
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Docs dialog ----------
export const DocsDialog = ({ open, onOpenChange, dispatchId, docs, onRefresh, isManager }) => {
  const [docType, setDocType] = useState('packing_list');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    if (!file) return toast.error('Select a file');
    if (file.size > 5 * 1024 * 1024) return toast.error('Max 5MB');
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const b64 = reader.result;
          await uploadDispatchDoc(dispatchId, {
            doc_type: docType, filename: file.name, content_base64: b64, mime_type: file.type,
          });
          toast.success('Uploaded');
          setFile(null);
          await onRefresh();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
        finally { setUploading(false); }
      };
      reader.readAsDataURL(file);
    } catch { setUploading(false); }
  };

  const download = async (docId) => {
    try {
      const { data } = await getDispatchDoc(dispatchId, docId);
      const link = document.createElement('a');
      link.href = data.content_base64;
      link.download = data.filename;
      link.click();
    } catch { toast.error('Download failed'); }
  };

  const remove = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try { await deleteDispatchDoc(dispatchId, docId); await onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border border-border rounded-none max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="docs-dialog">
        <DialogHeader>
          <DialogTitle>Dispatch documents</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {isManager && (
            <div className="border border-border p-3 grid grid-cols-3 gap-2">
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['packing_list', 'manifest', 'bol', 'transport_docket', 'load_sheet', 'label', 'other'].map(t => (
                    <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file-input" className="text-xs col-span-1" />
              <Button onClick={upload} disabled={!file || uploading} data-testid="upload-doc-btn" className="bg-brand text-white rounded-none">
                {uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </div>
          )}
          <div className="border border-border">
            <table className="w-full table-dense">
              <thead>
                <tr><th>Type</th><th>File</th><th>Uploaded</th><th></th></tr>
              </thead>
              <tbody>
                {docs.map(d => (
                  <tr key={d.doc_id} data-testid={`doc-${d.doc_id}`}>
                    <td className="text-xs font-mono">{d.doc_type}</td>
                    <td className="text-xs">{d.filename}</td>
                    <td className="text-xs text-muted-foreground font-mono">{new Date(d.uploaded_at).toLocaleString()}</td>
                    <td className="text-right">
                      <button onClick={() => download(d.doc_id)} className="text-brand mr-2" title="Download">
                        <DownloadSimple size={14} weight="bold" />
                      </button>
                      {isManager && (
                        <button onClick={() => remove(d.doc_id)} className="text-rose-600" title="Delete">
                          <Trash size={14} weight="bold" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {docs.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-muted-foreground text-xs">No documents.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ================= PHASE 2a DIALOGS =================

// ---------- Photo capture ----------
export const PhotoCaptureDialog = ({ open, onOpenChange, dispatchId, onUploaded }) => {
  const [category, setCategory] = useState('condition');
  const [caption, setCaption] = useState('');
  const [locationTag, setLocationTag] = useState('');
  const [base64, setBase64] = useState('');
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    const r = new FileReader();
    r.onload = () => setBase64(r.result);
    r.readAsDataURL(f);
  };
  const submit = async () => {
    if (!base64) return toast.error('Select a photo');
    setBusy(true);
    try {
      await addDispatchPhoto(dispatchId, { category, base64, caption: caption || null, location_tag: locationTag || null });
      toast.success('Photo captured');
      setBase64(''); setCaption(''); setLocationTag(''); setCategory('condition');
      onUploaded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border border-border rounded-none max-w-lg" data-testid="photo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera size={18} weight="fill" className="text-brand" /> Capture photo
          </DialogTitle>
          <DialogDescription className="text-xs">Tag the photo so filtering and audit work later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="photo-category" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['condition', 'damage', 'seal', 'loading', 'other'].map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Photo</Label>
            <input type="file" accept="image/*" capture="environment" onChange={onFile} data-testid="photo-file-input" className="text-xs" />
          </div>
          {base64 && <img src={base64} alt="preview" className="max-h-48 border border-border" />}
          <div>
            <Label>Caption</Label>
            <Input value={caption} onChange={(e) => setCaption(e.target.value)} className="rounded-none" />
          </div>
          <div>
            <Label>Location tag</Label>
            <Input value={locationTag} onChange={(e) => setLocationTag(e.target.value)} className="rounded-none font-mono" placeholder="e.g. Bay A-3 · Door rear" />
          </div>
          <Button onClick={submit} disabled={busy} data-testid="submit-photo-btn" className="bg-brand text-white rounded-none w-full">
            {busy ? 'Uploading…' : 'Save photo'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Photo gallery ----------
export const PhotoGalleryDialog = ({ open, onOpenChange, photos, dispatchId, isManager, onRefresh }) => {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? photos : photos.filter(p => p.category === filter);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border border-border rounded-none max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="photo-gallery">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageSquare size={18} weight="fill" className="text-brand" /> Photo gallery ({photos.length})
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-2 mb-2">
          {['all', 'condition', 'damage', 'seal', 'loading', 'other'].map(c => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`gal-filter-${c}`}
              className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${filter === c ? 'bg-brand text-white' : 'bg-muted text-foreground'}`}>
              {c}
            </button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-12 text-sm" data-testid="gallery-empty">No photos in this category.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {filtered.map(p => (
              <div key={p.photo_id} className="border border-border bg-muted" data-testid={`gal-item-${p.photo_id}`}>
                <img src={p.base64} alt={p.caption || p.category}
                  onClick={() => { const w = window.open('', '_blank'); if (w) w.document.write(`<img src="${p.base64}" style="max-width:100%"/>`); }}
                  className="w-full h-40 object-cover cursor-pointer border-b border-border" />
                <div className="p-2">
                  <p className={`text-[10px] uppercase font-bold tracking-widest ${p.category === 'damage' ? 'text-rose-700' : 'text-brand'}`}>
                    {p.category}
                  </p>
                  {p.caption && <p className="text-xs text-muted-foreground truncate">{p.caption}</p>}
                  {p.location_tag && <p className="text-[10px] text-muted-foreground font-mono">{p.location_tag}</p>}
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-muted-foreground font-mono">{new Date(p.uploaded_at).toLocaleString()}</span>
                    {isManager && (
                      <button onClick={async () => {
                        if (!window.confirm('Delete this photo?')) return;
                        try { await deleteDispatchPhoto(dispatchId, p.photo_id); await onRefresh(); }
                        catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
                      }} className="text-rose-600"><Trash size={11} weight="bold" /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------- Optimiser ----------
export const OptimiserDialog = ({ open, onOpenChange, dispatchId, isManager, onAllocated }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !dispatchId) return;
    (async () => {
      setBusy(true);
      try { const r = await getDispatchOptimise(dispatchId); setData(r.data); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Optimiser failed'); }
      finally { setBusy(false); }
    })();
  }, [open, dispatchId]);

  const addOrder = async (s) => {
    try {
      const { data: orders } = await getOrders();
      const o = (orders || []).find(x => x.id === s.order_id);
      if (!o) return toast.error('Order not found');
      const items = (o.items || []).map(it => ({
        sku_code: it.sku_code, variant_id: it.variant_id, qty: it.quantity,
        weight_kg_each: 0, volume_m3_each: 0,
      }));
      await allocateDispatchOrder(dispatchId, { order_id: s.order_id, items });
      toast.success(`Added ${s.order_number}`);
      onAllocated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Allocate failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border border-border rounded-none max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="optimise-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gauge size={18} weight="fill" className="text-amber-600" /> Weight/cube optimiser
          </DialogTitle>
          <DialogDescription className="text-xs">
            Suggests pending orders that would take this container closer to {data?.target_util_pct || 90}% util without exceeding capacity.
          </DialogDescription>
        </DialogHeader>
        {busy && <p className="py-8 text-center text-muted-foreground text-sm">Computing…</p>}
        {data && (
          <>
            <div className="grid grid-cols-4 gap-2 mt-2">
              <SumItem label="Current weight" value={`${data.current_util_weight_pct}%`} />
              <SumItem label="Current volume" value={`${data.current_util_volume_pct}%`} />
              <SumItem label="Avail. weight" value={`${data.available_weight_kg} kg`} accent="emerald" />
              <SumItem label="Avail. volume" value={`${data.available_volume_m3} m³`} accent="emerald" />
            </div>
            {(data.projected_util_weight_pct != null) && (
              <div className="grid grid-cols-3 gap-2 mt-2" data-testid="optimise-projection">
                <SumItem label="Proj. weight" value={`${data.projected_util_weight_pct}%`} accent="blue" />
                <SumItem label="Proj. volume" value={`${data.projected_util_volume_pct}%`} accent="blue" />
                <SumItem label="Wasted headroom" value={`${data.wasted_headroom_pct}%`} accent={data.wasted_headroom_pct > 20 ? 'amber' : 'emerald'} />
              </div>
            )}
            {data.orders_skipped_no_data > 0 && (
              <p className="text-xs text-amber-700 mt-2" data-testid="optimise-skipped">
                ⚠ {data.orders_skipped_no_data} order(s) skipped — no weight/volume data. Allocate them manually with weight_kg_each/volume_m3_each to enable optimiser.
              </p>
            )}
            {data.algorithm && (
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Algorithm · {data.algorithm.replace(/-/g, ' ')}</p>
            )}
            <div className="border border-border mt-3">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th>Order</th><th>Customer</th>
                    <th className="text-right">Lines</th>
                    <th className="text-right">Weight</th>
                    <th className="text-right">Volume</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(data.suggestions || []).map(s => (
                    <tr key={s.order_id} data-testid={`optimise-row-${s.order_number}`}>
                      <td className="font-mono font-bold text-brand">{s.order_number}</td>
                      <td className="text-xs">{s.customer_name}</td>
                      <td className="text-right font-mono">{s.line_count}</td>
                      <td className="text-right font-mono text-xs">{s.estimated_weight_kg}kg <span className="text-muted-foreground">({s.weight_fit_pct}%)</span></td>
                      <td className="text-right font-mono text-xs">{s.estimated_volume_m3}m³ <span className="text-muted-foreground">({s.volume_fit_pct}%)</span></td>
                      <td>
                        {isManager && (
                          <Button size="sm" onClick={() => addOrder(s)} data-testid={`optimise-add-${s.order_number}`} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none h-6 text-[10px]">
                            <Plus size={10} weight="bold" /> Add
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(data.suggestions || []).length === 0 && (
                    <tr><td colSpan={6} className="text-center py-6 text-muted-foreground text-sm">No fitting suggestions.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {(data.pre_pack_groups || []).length > 0 && (
              <div className="mt-3 border border-border" data-testid="optimise-prepack-groups">
                <div className="px-3 py-2 border-b border-border bg-muted flex items-center justify-between">
                  <span className="overline">Cartonization · Pre-pack waves by destination</span>
                  <span className="text-[10px] text-muted-foreground font-mono">{(data.pre_pack_groups || []).length} groups</span>
                </div>
                <table className="w-full table-dense">
                  <thead>
                    <tr>
                      <th>Destination</th>
                      <th className="text-right">Orders</th>
                      <th className="text-right">Weight</th>
                      <th className="text-right">Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.pre_pack_groups || []).map((g, i) => (
                      <tr key={i} data-testid={`prepack-group-${i}`}>
                        <td className="text-xs">{g.destination}</td>
                        <td className="text-right font-mono">{g.order_count}</td>
                        <td className="text-right font-mono text-xs">{g.total_weight_kg} kg</td>
                        <td className="text-right font-mono text-xs">{g.total_volume_m3} m³</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------- Dock booking ----------
export const DockBookingDialog = ({ open, onOpenChange, dispatchId, current, onSaved }) => {
  const [docks, setDocks] = useState([]);
  const [form, setForm] = useState({
    dock_code: current?.dock_code || '',
    start_at: current?.start_at ? new Date(current.start_at).toISOString().slice(0, 16) : '',
    duration_minutes: current?.duration_minutes || 60,
  });
  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const { data } = await listDocks(); setDocks(data || []); }
      catch { toast.error('Failed to load docks'); }
    })();
    setForm({
      dock_code: current?.dock_code || '',
      start_at: current?.start_at ? new Date(current.start_at).toISOString().slice(0, 16) : '',
      duration_minutes: current?.duration_minutes || 60,
    });
  }, [open, current]);
  const submit = async () => {
    if (!form.dock_code) return toast.error('Select a dock');
    if (!form.start_at) return toast.error('Pick start time');
    try {
      await bookDock(dispatchId, {
        dock_code: form.dock_code,
        start_at: new Date(form.start_at).toISOString(),
        duration_minutes: Number(form.duration_minutes) || 60,
      });
      toast.success('Dock booked');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Book failed'); }
  };
  const release = async () => {
    if (!window.confirm('Release this dock booking?')) return;
    try { await releaseDock(dispatchId); toast.success('Released'); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Release failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border border-border rounded-none max-w-md" data-testid="dock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarCheck size={18} weight="fill" className="text-brand" /> Book dock appointment
          </DialogTitle>
          <DialogDescription className="text-xs">Conflicting bookings on the same dock door return 409.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Dock door</Label>
            <Select value={form.dock_code} onValueChange={(v) => setForm({ ...form, dock_code: v })}>
              <SelectTrigger data-testid="dock-select" className="rounded-none"><SelectValue placeholder="Select dock…" /></SelectTrigger>
              <SelectContent>{docks.map(d => <SelectItem key={d.id} value={d.dock_code}>{d.dock_code}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Start time</Label>
            <Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="rounded-none" data-testid="dock-start" />
          </div>
          <div>
            <Label>Duration (min)</Label>
            <Input type="number" min={15} max={480} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="rounded-none" />
          </div>
          <div className="flex gap-2">
            <Button onClick={submit} data-testid="submit-dock-btn" className="bg-brand text-white rounded-none flex-1">
              {current ? 'Update booking' : 'Book'}
            </Button>
            {current && (
              <Button onClick={release} variant="outline" className="rounded-none border-rose-300 text-rose-700">
                Release
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Manual shipping (dispatch) ----------
export const ManualShippingDialog = ({ open, onOpenChange, dispatchId, current, onSaved }) => {
  const [form, setForm] = useState(current || {});
  useEffect(() => { setForm(current || {}); }, [current, open]);
  const submit = async () => {
    if (!form.carrier_name?.trim()) return toast.error('Carrier name required');
    try {
      await bookManualShipping(dispatchId, {
        carrier_name: form.carrier_name.trim(),
        tracking_number: form.tracking_number || null,
        service_level: form.service_level || null,
        shipping_cost: form.shipping_cost !== '' && form.shipping_cost != null ? Number(form.shipping_cost) : null,
        shipping_currency: form.shipping_currency || 'USD',
        pickup_at: form.pickup_at ? new Date(form.pickup_at).toISOString() : null,
        estimated_delivery_at: form.estimated_delivery_at ? new Date(form.estimated_delivery_at).toISOString() : null,
        notes: form.notes || null,
        contact_name: form.contact_name || null,
        contact_phone: form.contact_phone || null,
      });
      toast.success('Manual shipping booked');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const remove = async () => {
    if (!window.confirm('Remove manual shipping?')) return;
    try { await removeManualShipping(dispatchId); toast.success('Removed'); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border border-border rounded-none max-w-xl max-h-[90vh] overflow-y-auto" data-testid="shipping-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TruckIcon size={18} weight="fill" className="text-brand" /> Manual shipping
          </DialogTitle>
          <DialogDescription className="text-xs">Record carrier details when you don't use an integrated carrier API.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Carrier *</Label><Input value={form.carrier_name || ''} onChange={(e) => setForm({ ...form, carrier_name: e.target.value })} className="rounded-none" data-testid="ship-carrier" /></div>
          <div><Label>Tracking #</Label><Input value={form.tracking_number || ''} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} className="rounded-none font-mono" data-testid="ship-tracking" /></div>
          <div><Label>Service level</Label><Input value={form.service_level || ''} onChange={(e) => setForm({ ...form, service_level: e.target.value })} className="rounded-none" placeholder="Express / Standard / Economy" /></div>
          <div><Label>Cost</Label>
            <div className="flex gap-1">
              <Input type="number" step="0.01" value={form.shipping_cost ?? ''} onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })} className="rounded-none flex-1" />
              <Input value={form.shipping_currency || 'USD'} onChange={(e) => setForm({ ...form, shipping_currency: e.target.value.toUpperCase().slice(0, 4) })} className="rounded-none w-16 font-mono" />
            </div>
          </div>
          <div><Label>Pickup at</Label><Input type="datetime-local" value={form.pickup_at ? new Date(form.pickup_at).toISOString().slice(0, 16) : ''} onChange={(e) => setForm({ ...form, pickup_at: e.target.value })} className="rounded-none" /></div>
          <div><Label>ETA</Label><Input type="datetime-local" value={form.estimated_delivery_at ? new Date(form.estimated_delivery_at).toISOString().slice(0, 16) : ''} onChange={(e) => setForm({ ...form, estimated_delivery_at: e.target.value })} className="rounded-none" /></div>
          <div><Label>Contact name</Label><Input value={form.contact_name || ''} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-none" /></div>
          <div><Label>Contact phone</Label><Input value={form.contact_phone || ''} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="rounded-none font-mono" /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" /></div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button onClick={submit} data-testid="submit-shipping-btn" className="bg-brand text-white rounded-none flex-1">
            {current ? 'Update' : 'Book shipping'}
          </Button>
          {current && (
            <Button onClick={remove} variant="outline" className="rounded-none border-rose-300 text-rose-700">Remove</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ---------- Phase 3: Dock Appointment Conflict Heatmap ----------
export const DockHeatmapDialog = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(7);
  const [slotHours, setSlotHours] = useState(2);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Auto-resolve state
  const [resolveBusy, setResolveBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {dry_run, proposed, counts...}
  const [applying, setApplying] = useState(false);

  const load = React.useCallback(async () => {
    setBusy(true);
    try {
      const r = await getDocksHeatmap({ days, slot_hours: slotHours, start_date: startDate });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load heatmap');
    } finally { setBusy(false); }
  }, [days, slotHours, startDate]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Colour map by utilisation
  const cellColor = (cell) => {
    if (cell.conflict) return 'bg-rose-600 text-white';
    if (cell.booking_count === 0) return 'bg-muted text-slate-300';
    if (cell.utilisation_pct >= 80) return 'bg-amber-500 text-white';
    if (cell.utilisation_pct >= 40) return 'bg-amber-200 text-amber-900';
    return 'bg-emerald-200 text-emerald-900';
  };

  const dayLabels = React.useMemo(() => {
    if (!data?.start_date) return [];
    const out = [];
    const d0 = new Date(`${data.start_date}T00:00:00Z`);
    for (let i = 0; i < (data.days || 0); i++) {
      const d = new Date(d0.getTime() + i * 24 * 3600 * 1000);
      out.push(d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: '2-digit', timeZone: 'UTC' }));
    }
    return out;
  }, [data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border border-border rounded-none max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="dock-heatmap-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gauge size={18} weight="fill" className="text-brand" /> Dock Appointment Heatmap
          </DialogTitle>
          <DialogDescription className="text-xs">
            Per-dock × per-slot booking density. Rose cells = conflicting double-books, amber = tight, green = normal.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3 mt-2 pb-3 border-b border-border">
          <label className="text-xs">
            <span className="overline block mb-1">Start</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="heatmap-start-date"
              className="px-2 py-1 border border-border font-mono text-xs" />
          </label>
          <label className="text-xs">
            <span className="overline block mb-1">Days</span>
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value))} data-testid="heatmap-days"
              className="px-2 py-1 border border-border font-mono text-xs">
              <option value={3}>3</option><option value={7}>7</option><option value={14}>14</option><option value={28}>28</option>
            </select>
          </label>
          <label className="text-xs">
            <span className="overline block mb-1">Slot (h)</span>
            <select value={slotHours} onChange={(e) => setSlotHours(parseInt(e.target.value))} data-testid="heatmap-slot"
              className="px-2 py-1 border border-border font-mono text-xs">
              {[1, 2, 3, 4, 6, 8, 12, 24].map(s => <option key={s} value={s}>{s}h</option>)}
            </select>
          </label>
          <Button size="sm" onClick={load} disabled={busy} data-testid="heatmap-refresh"
            className="bg-brand text-white rounded-none h-[30px]">
            {busy ? 'Loading…' : 'Refresh'}
          </Button>

          {data && (
            <div className="ml-auto flex gap-4 text-xs" data-testid="heatmap-summary">
              <span><b className="font-mono">{data.dock_count}</b> docks</span>
              <span><b className="font-mono">{data.total_bookings}</b> bookings</span>
              <span className={data.total_conflicts > 0 ? 'text-rose-600 font-bold' : ''}>
                <b className="font-mono">{data.total_conflicts}</b> conflicts
              </span>
              {data.total_conflicts > 0 && (
                <Button size="sm" variant="outline" disabled={resolveBusy}
                  data-testid="heatmap-auto-resolve-btn"
                  className="rounded-none h-[28px] border-rose-400 text-rose-700 hover:bg-rose-600 hover:text-white"
                  onClick={async () => {
                    setResolveBusy(true);
                    try {
                      const r = await heatmapAutoResolve({
                        days, start_date: startDate, apply: false,
                      });
                      setPreview(r.data);
                      if (!r.data.proposed_count) toast.info('No conflicts found');
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Auto-resolve failed');
                    } finally { setResolveBusy(false); }
                  }}>
                  <ArrowsCounterClockwise size={13} weight="bold" className="mr-1" />
                  {resolveBusy ? 'Scanning…' : 'Auto-resolve conflicts'}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Auto-resolve preview modal-inline */}
        {preview && (
          <div className="mt-3 border-2 border-rose-400 bg-rose-50 p-3" data-testid="heatmap-auto-resolve-preview">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-bold tracking-tight">
                Auto-resolve preview · {preview.resolved_count}/{preview.proposed_count} resolvable
              </p>
              <button type="button" onClick={() => setPreview(null)} data-testid="heatmap-auto-resolve-cancel"
                className="text-xs uppercase tracking-widest text-muted-foreground hover:text-slate-800 font-bold">Cancel</button>
            </div>
            <div className="max-h-52 overflow-y-auto bg-card border border-rose-200">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th>Dispatch</th>
                    <th>Dock</th>
                    <th>Before</th>
                    <th>After</th>
                    <th className="text-right">Shift</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.proposed.map((p, i) => (
                    <tr key={i} data-testid={`heatmap-proposed-${i}`} className={p.resolved ? '' : 'bg-muted text-muted-foreground'}>
                      <td className="font-mono text-[11px]">{p.dispatch_number}</td>
                      <td className="font-mono text-[11px]">{p.dock_code}</td>
                      <td className="font-mono text-[10px]">{p.before.start_at.slice(5, 16).replace('T', ' ')}</td>
                      <td className={`font-mono text-[10px] ${p.resolved ? 'text-emerald-700 font-bold' : ''}`}>
                        {p.after ? p.after.start_at.slice(5, 16).replace('T', ' ') : <span className="text-rose-500">— {p.reason}</span>}
                      </td>
                      <td className="text-right font-mono text-[10px]">{p.shift_minutes != null ? `${p.shift_minutes}m` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => setPreview(null)}
                className="rounded-none h-[30px]">Cancel</Button>
              <Button size="sm" disabled={applying || preview.resolved_count === 0}
                data-testid="heatmap-auto-resolve-apply"
                onClick={async () => {
                  if (!window.confirm(`Apply ${preview.resolved_count} reschedule(s)? Audit log will be updated.`)) return;
                  setApplying(true);
                  try {
                    const r = await heatmapAutoResolve({ days, start_date: startDate, apply: true });
                    toast.success(`Applied ${r.data.applied_count} reschedule(s)`);
                    setPreview(null);
                    await load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Apply failed');
                  } finally { setApplying(false); }
                }}
                className="bg-rose-600 text-white rounded-none hover:bg-rose-700 h-[30px]">
                {applying ? 'Applying…' : `Apply ${preview.resolved_count} moves`}
              </Button>
            </div>
          </div>
        )}

        {busy && !data && <p className="py-10 text-center text-muted-foreground text-sm">Loading heatmap…</p>}

        {data && (data.grid || []).length === 0 && (
          <p className="py-10 text-center text-muted-foreground text-sm" data-testid="heatmap-empty">No outbound docks defined.</p>
        )}

        {data && (data.grid || []).length > 0 && (
          <div className="overflow-x-auto mt-3">
            <table className="border-collapse" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              <thead>
                <tr>
                  <th className="sticky left-0 bg-card z-10 p-1 text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border" rowSpan={2}>Dock</th>
                  {dayLabels.map((d, di) => (
                    <th key={di} colSpan={data.slots_per_day} className="text-[10px] font-bold text-foreground border-b border-border px-1 py-1">
                      {d}
                    </th>
                  ))}
                </tr>
                <tr>
                  {Array.from({ length: data.days }).map((_, di) =>
                    Array.from({ length: data.slots_per_day }).map((_, si) => (
                      <th key={`${di}-${si}`} className="text-[8px] text-muted-foreground font-normal border-b border-border py-0.5" style={{ minWidth: 18 }}>
                        {si * data.slot_hours}
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {data.grid.map((row) => (
                  <tr key={row.dock_code}>
                    <td className="sticky left-0 bg-card font-bold text-xs pr-2 py-0.5 border-b border-border" data-testid={`heatmap-row-${row.dock_code}`}>
                      {row.dock_code}
                    </td>
                    {row.cells.map((cell, ci) => (
                      <td
                        key={ci}
                        title={cell.booking_count === 0
                          ? `Free · ${cell.slot_start.replace('T', ' ').slice(0, 16)}`
                          : `${cell.booking_count} booking${cell.booking_count === 1 ? '' : 's'} · ${cell.utilisation_pct}% util${cell.conflict ? ' · CONFLICT' : ''}\n${cell.bookings.map(b => `${b.dispatch_number} (${b.customer_name || '—'})`).join('\n')}`
                        }
                        data-testid={cell.conflict ? `heatmap-conflict-${row.dock_code}-${cell.day_idx}-${cell.slot_idx}` : undefined}
                        className={`text-[8px] font-bold text-center border border-white ${cellColor(cell)}`}
                        style={{ minWidth: 18, height: 22 }}
                      >
                        {cell.conflict ? '!' : cell.booking_count > 0 ? cell.booking_count : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-4 mt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-muted border border-border"></span> Free</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-200"></span> &lt;40%</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-amber-200"></span> 40–80%</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-amber-500"></span> ≥80%</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-rose-600"></span> Conflict</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------- Phase 3c: Carrier Portal Handoff ----------
export const CarrierHandoffDialog = ({ open, onOpenChange, dispatchId, dispatchNumber }) => {
  const [tokens, setTokens] = useState([]);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [ttl, setTtl] = useState(72);
  const [note, setNote] = useState('');
  const [lastGenerated, setLastGenerated] = useState(null);

  const load = React.useCallback(async () => {
    if (!dispatchId) return;
    try {
      const r = await listCarrierHandoffs(dispatchId);
      setTokens(r.data || []);
    } catch { /* ignore */ }
  }, [dispatchId]);

  useEffect(() => { if (open) { load(); setLastGenerated(null); } }, [open, load]);

  const generate = async () => {
    if (!dispatchId) return;
    setBusy(true);
    try {
      const r = await generateCarrierHandoff(dispatchId, {
        recipient_email: email.trim() || null,
        recipient_name: name.trim() || null,
        ttl_hours: ttl,
        note: note.trim() || null,
      });
      setLastGenerated(r.data);
      toast.success(r.data.email_sent ? 'Carrier link generated & emailed' : 'Carrier link generated');
      setEmail(''); setName(''); setNote('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally { setBusy(false); }
  };

  const revoke = async (token) => {
    if (!window.confirm('Revoke this link? Carrier will no longer be able to access it.')) return;
    try {
      await revokeCarrierHandoff(dispatchId, token);
      toast.success('Link revoked');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(
      () => toast.success('Copied to clipboard'),
      () => toast.error('Copy failed — select manually'),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border border-border rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="carrier-handoff-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={18} weight="fill" className="text-brand" /> Carrier Portal Handoff
          </DialogTitle>
          <DialogDescription className="text-xs">
            Generate a time-limited signed URL a carrier can open on any device (no login) to view the manifest + confirm pickup with a signature.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-3 mt-2 pb-3 border-b border-border">
          <div>
            <Label className="overline" htmlFor="handoff-name">Recipient name</Label>
            <Input id="handoff-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="John Driver" data-testid="handoff-name" className="rounded-none" />
          </div>
          <div>
            <Label className="overline" htmlFor="handoff-email">Recipient email (mock)</Label>
            <Input id="handoff-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="driver@carrier.test" data-testid="handoff-email" className="rounded-none" />
          </div>
          <div>
            <Label className="overline" htmlFor="handoff-ttl">Valid for (hours)</Label>
            <Input id="handoff-ttl" type="number" min={1} max={168} value={ttl} onChange={(e) => setTtl(parseInt(e.target.value || '72'))}
              data-testid="handoff-ttl" className="rounded-none font-mono" />
          </div>
          <div>
            <Label className="overline" htmlFor="handoff-note">Note (optional)</Label>
            <Input id="handoff-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Gate B · ring bell on arrival" data-testid="handoff-note" className="rounded-none" />
          </div>
          <div className="md:col-span-2">
            <Button onClick={generate} disabled={busy || !dispatchId}
              data-testid="handoff-generate-btn"
              className="bg-brand text-white rounded-none hover:bg-[#0040d9] w-full">
              {busy ? 'Generating…' : `Generate signed link${email ? ' & mock-email' : ''}`}
            </Button>
          </div>
        </div>

        {lastGenerated && (
          <div className="bg-emerald-50 border border-emerald-300 p-3 my-3" data-testid="handoff-last-generated">
            <p className="overline text-emerald-800 mb-1">New link ready</p>
            <div className="flex items-center gap-2">
              <input readOnly value={lastGenerated.public_url}
                className="flex-1 px-2 py-1 border border-emerald-300 bg-card font-mono text-xs" />
              <Button size="sm" onClick={() => copyUrl(lastGenerated.public_url)} variant="outline"
                data-testid="handoff-copy-btn" className="rounded-none h-[32px]">
                Copy
              </Button>
              <a href={lastGenerated.public_url} target="_blank" rel="noreferrer"
                data-testid="handoff-open-btn"
                className="text-xs font-bold uppercase tracking-widest px-2 py-2 border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition">
                Open
              </a>
            </div>
            <p className="text-[10px] text-emerald-800 font-mono mt-1">
              Expires {new Date(lastGenerated.expires_at).toLocaleString()}{lastGenerated.email_sent ? ' · email delivered (mock)' : ''}
            </p>
          </div>
        )}

        <div className="mt-3">
          <p className="overline text-muted-foreground mb-2">History · {dispatchNumber}</p>
          {tokens.length === 0 ? (
            <p className="text-xs text-muted-foreground py-6 text-center" data-testid="handoff-history-empty">No links generated yet.</p>
          ) : (
            <table className="w-full table-dense">
              <thead>
                <tr>
                  <th>Token</th>
                  <th>Recipient</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th className="text-right">Views</th>
                  <th>POP</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((t, i) => {
                  const expired = new Date(t.expires_at) < new Date();
                  const revoked = t.revoked;
                  return (
                    <tr key={t.token} data-testid={`handoff-row-${i}`}>
                      <td className="font-mono text-xs">{t.token_preview}</td>
                      <td className="text-xs">{t.recipient_name || '—'} <span className="text-muted-foreground">{t.recipient_email || ''}</span></td>
                      <td className="font-mono text-[10px]">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</td>
                      <td className="font-mono text-[10px]">{t.expires_at ? new Date(t.expires_at).toLocaleDateString() : '—'}</td>
                      <td className="font-mono text-right">{t.view_count ?? 0}</td>
                      <td>{t.pop_submitted ? <span className="text-emerald-700 font-bold text-xs">✓</span> : <span className="text-muted-foreground text-xs">—</span>}</td>
                      <td>
                        <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                          revoked ? 'bg-rose-100 text-rose-700' : expired ? 'bg-muted text-muted-foreground' : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {revoked ? 'revoked' : expired ? 'expired' : 'active'}
                        </span>
                      </td>
                      <td className="text-right">
                        {!revoked && !expired && (
                          <button type="button" onClick={() => revoke(t.token)} data-testid={`handoff-revoke-${i}`}
                            className="text-[10px] font-bold text-rose-600 hover:text-rose-700 uppercase tracking-widest">
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

